import React, { useState, useEffect } from 'react'
import { Link, useParams, Outlet } from 'react-router'
import { FaCheckCircle, FaExclamationTriangle } from 'react-icons/fa'
import { Stack, Loading, Box, Tag, Icon } from '@/components/lib'
import api from '../../api'
import utils from '../../utils/utils.js'
import useTitle from '../../hooks/useTitle'
import useStore from '../../store'

import VerticalNavSystem from '../includes/VerticalNavSystem'
import Login from '../entry/Login'
import Assistant from '../trials/Assistant'

const SideDivider = () => (
  <Box height='1px' mt={2} mb={2} bg='border.muted' />
)

const SideLink = ({ active, to, content, children, className, numberValue, numberColorPalette }) => {
  return (
    <Box
      rounded='sm'
      display='flex' alignItems='center' justifyContent='space-between'
      as={Link} to={to}
      style={{ padding: '5px 10px' }}
      bg={active ? 'primary.muted' : null}
      color={active ? 'primary.fg' : 'fg.muted'}
      fontWeight={active ? 'semibold' : 'normal'}
      transition='0.2s'
      _hover={{ bg: 'primary.subtle', color: 'primary.fg' }}
      fontSize='sm'
      className={`${active ? 'active' : ''} ${className}`}
    >
      {content || children}
      {numberValue !== undefined && <Tag borderRadius='full' ml={2} size='sm' colorPalette={numberColorPalette || 'gray'} variant='solid'>{numberValue || 0}</Tag>}
    </Box>
  )
}

export default function AdminDashboard ({ children }) {
  const [loading, setLoading] = useState(false)
  const { setTrial, team, user, trial, stages, dataTypes, addDataType, addTrialUser, addSite, addGroup, setAnchors, setSiteUnreads } = useStore()
  const { id: trialId } = useParams()
  useTitle(trial?.name || 'Trial')

  useEffect(() => {
    if (!trialId || !user) return
    setTrial(null)
    setLoading(true)
    api.trials.get(trialId, trial => {
      setLoading(false)
      setTrial(trial)

      // Fetch users belongong to the trial (used for assignment, queries, etc.)
      api.trials.getUsers(trialId, d => d.users?.forEach(u => addTrialUser(trialId, u)))
      // Fetch sites belonging to the trial (used for assignment, queries, etc.)
      api.sites.getForTrial(trialId, d => d.sites?.forEach(s => addSite(s)))
      // Seed per-site unread message-board counts (drives the navbar bubble)
      api.sites.getUnreadCounts(trialId, d => setSiteUnreads(d.sites), () => {})

      // Fetch groups for the trial
      api.trials.getGroups(trialId, d => d.groups?.forEach(g => addGroup(g)))

      // Fetch data types for the trial
      api.trials.getDataTypes(trialId, d => d.dataTypes?.forEach(dt => addDataType(dt)))

      // Fetch anchors for the trial
      api.anchors.list(trialId, d => setAnchors(d.anchors || []))
    })
  }, [trialId, user])

  if (!user) return <Login withWarning />

  let hasQuestionnaireStage = false
  stages?.forEach(stage => {
    if (stage.isPoll) hasQuestionnaireStage = true
  })

  // The orange "not configured yet" triangles are only actionable by people who
  // can edit the trial, so hide them from view-only users.
  const canWrite = trial ? utils.canWriteTrial(user, trial) : false

  return (
    <VerticalNavSystem
      nav={
        trial &&
          <Stack>
            <Stack gap={5} p={3} className='joyride-sidebar'>
              <Stack gap={1}>
                <SideLink active={utils.activePath('manage')} as={Link} to={`/trials/${trial._id}/manage`} content='Overview' />
                <SideLink className='joyride-trial-info' active={utils.activePath('info')} as={Link} to={`/trials/${trial._id}/info`}>
                  About
                  {(trial.name && trial.description && trial.code) ? <Icon as={FaCheckCircle} color='green.500' _dark={{ color: 'green.400' }} ml={2} /> : (canWrite && <Icon as={FaExclamationTriangle} color='orange.500' _dark={{ color: 'orange.400' }} ml={2} />)}
                </SideLink>
                {utils.hasFeature(team, 'eTmf') &&
                  <SideLink active={utils.activePath('files')} as={Link} to={`/trials/${trial._id}/files`}>
                    Documents
                  </SideLink>}

                {utils.canReadTrialUsers(user, trial) &&
                  <>
                    <SideLink active={utils.activePath('personnel')} as={Link} to={`/trials/${trial._id}/personnel`}>
                      Personnel
                    </SideLink>
                  </>}

                {utils.canReadTrialSettings(user, trial) &&
                  <SideLink className='joyride-trial-settings' active={utils.activePath('settings')} as={Link} to={`/trials/${trial._id}/settings`} content='Settings' />}
                {(utils.canReadTrialSettings(user, trial) || utils.canReadDataTypes(user, trial) || utils.canReadStages(user, trial)) &&
                  <SideDivider />}
                {utils.canReadDataTypes(user, trial) &&
                  <SideLink className='joyride-trial-data' active={utils.activePath('data')} as={Link} to={`/trials/${trial._id}/data`}>
                    Data Types
                    {(dataTypes?.length ?? 0) > 0
                      ? <Tag borderRadius='full' ml={2} size='sm' colorPalette='gray' variant='solid'>{dataTypes.length}</Tag>
                      : (canWrite && <Icon as={FaExclamationTriangle} color='orange.500' _dark={{ color: 'orange.400' }} ml={2} />)}
                  </SideLink>}
                {utils.canReadStages(user, trial) &&
                  <SideLink className='joyride-trial-stages' active={utils.activePath('stages')} as={Link} to={`/trials/${trial._id}/stages`}>
                    Stages
                    {(stages?.length ?? 0) > 0
                      ? <Tag borderRadius='full' ml={2} size='sm' colorPalette='gray' variant='solid'>{stages.length}</Tag>
                      : (canWrite && <Icon as={FaExclamationTriangle} color='orange.500' _dark={{ color: 'orange.400' }} ml={2} />)}
                  </SideLink>}
                {utils.hasFeature(team, 'eConsent') && (utils.canReadTrialConsents(user, trial) || utils.canReadConsentSettings(user, trial)) &&
                  <SideLink active={utils.activePath('trials/[^/]+/consent')} as={Link} to={`/trials/${trial._id}/consent`}>
                    Consent
                    {trial.eConsentEnabled && <Icon as={FaCheckCircle} color='green.500' _dark={{ color: 'green.400' }} ml={2} />}
                  </SideLink>}
                {utils.canReadTrialSites(user, trial) &&
                  <SideLink active={utils.activePath('sites')} as={Link} to={`/trials/${trial._id}/sites`} numberValue={trial.siteCount}>
                    Sites
                  </SideLink>}
                <SideDivider />

                {utils.hasFeature(team, 'simplyBookEmbed') && utils.canManageTrial(user, trial) &&
                  <SideLink active={utils.activePath('booking')} as={Link} to={`/trials/${trial._id}/booking`}>
                    Booking
                  </SideLink>}

                {utils.canListPacksTrial(user, trial) && utils.hasFeature(team, 'packs') &&
                  <SideLink active={utils.activePath('packs')} as={Link} to={`/trials/${trial._id}/packs`}>Packs</SideLink>}
                {utils.canReadTrialQueries(user, trial) && utils.hasFeature(team, 'queries') &&
                  <SideLink
                    active={utils.activePath('queries')}
                    as={Link}
                    to={`/trials/${trial._id}/queries`}
                    // Only when there is something waiting: a red zero is noise,
                    // and the point of the count is that it demands attention.
                    numberValue={trial.openQueryCount > 0 ? trial.openQueryCount : undefined}
                    numberColorPalette='red'
                  >
                    Queries
                  </SideLink>}
                {utils.hasFeature(team, 'tasks') && utils.canReadTasks(user, trial) &&
                  <SideLink active={utils.activePath('tasks')} as={Link} to={`/trials/${trial._id}/tasks`}>Tasks</SideLink>}
                {utils.canReadParticipantStudyData(user, trial) &&
                  <>
                    {hasQuestionnaireStage &&
                      <SideLink active={utils.activePath('poll-responses')} as={Link} to={`/trials/${trial._id}/poll-responses`} content='Questionnaires' />}
                    <SideLink className='joyride-trial-results' active={utils.activePath('results')} as={Link} to={`/trials/${trial._id}/results`} content='Results' numberValue={trial.responseCount} />
                  </>}
                {utils.canAccessTrial(user, trial) &&
                  <>
                    <SideLink className='joyride-trial-participants' active={window.location.pathname.indexOf(`/trials/${trial._id}/participants`) === 0} as={Link} to={`/trials/${trial._id}/participants`} numberValue={trial.participantCount}>
                      Participants
                    </SideLink>
                  </>}
                {utils.canSupportTrial(user, trial) &&
                  <>
                    <SideLink className='joyride-trial-messages' active={utils.activePath('trials/[^/]+/messages')} as={Link} to={`/trials/${trial._id}/messages`} content='Messages' />
                  </>}
              </Stack>
            </Stack>
          </Stack>
}
      body={
        <Box>
          {loading
            ? <Box textAlign='center'><Loading /></Box>
            : (trial && <Outlet />)}
          <Assistant />
        </Box>
}
    />
  )
}
